/* eslint-disable max-statements */
import { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { message } from 'antd';

import { MODE, WorkCenterBaseline } from 'models/baselines';
import { appInsights } from '@exxonmobil/uit-uda-ui-components-lib';
import { BaselinesContext } from 'providers/BaselinesProvider';
import {
  getFulfilledResponse,
  getWorkCentersWithSearchedCompany,
  getWorkCentersWithSearchedWorkCenter
} from './Baselines.helpers';
import { ACTION, PromiseAllResponse } from './Baselines.types';

const useBaselines = () => {
  const {
    updateBaselineSelection,
    updateBaselineSelectionAll,
    updateMode,
    cloneApprovedToRequested,
    stateRecord: baselinesStateRecord,
    workCenters,
    isUserApprover,
    approveSelected,
    rejectSelected
  } = useContext(BaselinesContext);
  const { data: fullBaselineData } = baselinesStateRecord.state;
  const [searchBaselines, setSearchBaselines] = useState('');
  const [filteredBaselines, setFilteredBaselines] =
    useState<WorkCenterBaseline[]>(fullBaselineData);
  const [selectionChangeAll, setSelectionChangeAll] = useState({
    isSelected: false
  });
  const [numberOfBaselinesRequested, setNumberOfBaselinesRequested] =
    useState(0);
  const [allSelected, setAllSelected] = useState(false);
  const [promiseAllResponse, setPromiseAllResponse] =
    useState<PromiseAllResponse>({
      action: ACTION.APPROVE,
      successResponses: 0,
      failedResponses: []
    });

  useEffect(() => {
    document.title = 'AION // Baselines';
    appInsights.trackPageView({ name: 'Baselines' });
  }, []);

  const onSearchChangeHandler = (searchTerm: string) => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    setSearchBaselines(searchTerm);
    // clear selected items by checkbox
    setSelectionChangeAll({
      isSelected: false
    });
    // filter items that have assosiated companies in ID or Company field for that workcenter
    const filteredBaselinesByCompany: WorkCenterBaseline[] = [];
    workCenters.forEach((wc) => {
      wc.companies.forEach((companyId) => {
        filteredBaselinesByCompany.push(
          ...getWorkCentersWithSearchedCompany(
            fullBaselineData,
            lowerCaseSearchTerm,
            companyId,
            wc
          )
        );
      });
    });
    const filteredBaselinesByWorkCenter = getWorkCentersWithSearchedWorkCenter(
      fullBaselineData,
      lowerCaseSearchTerm
    );
    const combinedFilteredBaselines: WorkCenterBaseline[] =
      filteredBaselinesByWorkCenter.concat(filteredBaselinesByCompany);

    setFilteredBaselines(_.uniq(combinedFilteredBaselines));
  };

  const disableApproveRejectButton = () => {
    const selectedWorkCenters = fullBaselineData.filter((x) => x.isSelected);
    if (selectedWorkCenters.length > 0) {
      return false; // disable equals false, when something is selected.  i.e. Enable = true
    }
    return true;
  };

  const onCheckboxClickHandler = (isSelected: boolean, workCenter: string) => {
    updateBaselineSelection(isSelected, workCenter);
  };

  const onCheckAllClickHandler = (isSelected: boolean) => {
    updateBaselineSelectionAll(isSelected);
    setSelectionChangeAll({ isSelected });
  };

  const onWorkCentersPendingClickHandler = () => {
    onCheckAllClickHandler(true);
    setAllSelected(true);
  };

  const onClearFiltersClickHandler = () => {
    onSearchChangeHandler('');
    onCheckAllClickHandler(false);
    setAllSelected(false);
  };

  const onSelectAllClickHandler = () => {
    onCheckAllClickHandler(!allSelected);
    setAllSelected(!allSelected);
  };

  const updateBaselineMode = (props: { mode: MODE; workCenter: string }) => {
    filteredBaselines.forEach((baseline: WorkCenterBaseline) => {
      _.set(baseline, ['mode'], '');
      if (baseline.workCenter === props.workCenter) {
        _.set(baseline, ['mode'], props.mode);
      }
    });
  };

  const onIconClickHandler = (mode: MODE, workCenter: string) => {
    updateBaselineMode({ mode, workCenter });
    updateMode(mode, workCenter);
    cloneApprovedToRequested(mode, workCenter);
  };

  useEffect(() => {
    if (searchBaselines) {
      onSearchChangeHandler(searchBaselines);
    }
    let numberRequested = 0;
    fullBaselineData.forEach((element) => {
      if (element.requested && element.requested.length > 0) {
        numberRequested += 1;
      }
    });
    setNumberOfBaselinesRequested(numberRequested);
  }, [baselinesStateRecord.state]);

  const [messageApi, contextHolder] = message.useMessage();
  useEffect(() => {
    if (promiseAllResponse.successResponses) {
      messageApi.success(
        `Successfully ${promiseAllResponse.action} ${promiseAllResponse.successResponses} baselines`,
        5
      );
    }

    if (promiseAllResponse.failedResponses.length) {
      const errorMessages = promiseAllResponse.failedResponses.map((resp) => {
        if (resp === 422) {
          return 'No baselines to approve';
        }
        if (resp === 404) {
          return 'Work Center not found';
        }
        if (resp === 401) {
          return 'You are not authorized to approve requests';
        }
        return 'Server error, please try again';
      });
      messageApi.error(
        `${errorMessages.length} baselines not ${
          promiseAllResponse.action
        } due to ${errorMessages.join(', ')}`,
        5
      );
    }
  }, [promiseAllResponse]);

  const onApprovalClickHandler = async (action: ACTION) => {
    let results;
    if (action === ACTION.APPROVE) {
      results = await approveSelected();
    } else {
      results = await rejectSelected();
    }

    const fulfilledResponses = getFulfilledResponse(results);

    setPromiseAllResponse({
      action,
      successResponses: fulfilledResponses.filter((resp) => resp === 200)
        .length,
      failedResponses: fulfilledResponses.filter((resp) => resp !== 200)
    });
  };

  return {
    searchBaselines,
    filteredBaselines,
    selectionChangeAll,
    numberOfBaselinesRequested,
    allSelected,
    isUserApprover,
    contextHolder,
    promiseAllResponse,
    onSearchChangeHandler,
    onWorkCentersPendingClickHandler,
    onClearFiltersClickHandler,
    onSelectAllClickHandler,
    onIconClickHandler,
    onCheckboxClickHandler,
    onApprovalClickHandler,
    disableApproveRejectButton
  };
};

export default useBaselines;
